import { Box, ButtonIcon, Flex, Text } from '@backstage/ui';
import CloseIcon from '@material-ui/icons/Close';
import type { LeaderboardEntry } from './types';
import {
  RELATION_PATCH_GAME_TITLE,
  RELATION_PATCH_LEADERBOARD_SUBTITLE,
  RELATION_PATCH_LEADERBOARD_TITLE,
} from './gameCopy';
import styles from './GraphRunnerGame.module.css';

interface GraphRunnerLeaderboardProps {
  entries: LeaderboardEntry[];
  loading: boolean;
  error: string | null;
  canRemoveScores?: boolean;
  onRemoveScore?: (userEntityRef: string, displayName: string) => void;
}

function leaderboardBody({
  entries,
  loading,
  error,
  canRemoveScores = false,
  onRemoveScore,
}: GraphRunnerLeaderboardProps) {
  if (loading) {
    return (
      <Text variant="body-small" className={styles.leaderboardMessage}>
        Loading leaderboard…
      </Text>
    );
  }

  if (error) {
    return (
      <Text variant="body-small" className={styles.leaderboardMessage}>
        {error}
      </Text>
    );
  }

  if (entries.length === 0) {
    return (
      <Text variant="body-small" className={styles.leaderboardMessage}>
        No scores yet - patch the first relationship to claim the top spot!
      </Text>
    );
  }

  return (
    <ol className={styles.leaderboardList}>
      {entries.map(entry => (
        <li
          key={entry.userEntityRef}
          className={
            entry.isCurrentUser
              ? styles.leaderboardItemCurrent
              : styles.leaderboardItem
          }
        >
          <Text
            variant="body-small"
            weight="bold"
            className={styles.leaderboardRank}
          >
            {entry.rank}
          </Text>
          <Text variant="body-small" className={styles.leaderboardName}>
            {entry.displayName}
          </Text>
          <Text
            variant="body-small"
            weight="bold"
            className={styles.leaderboardScore}
          >
            {entry.score}
          </Text>
          {canRemoveScores && onRemoveScore && (
            <ButtonIcon
              variant="tertiary"
              size="small"
              className={styles.leaderboardRemove}
              aria-label={`Remove ${entry.displayName}'s score`}
              onPress={() =>
                onRemoveScore(entry.userEntityRef, entry.displayName)
              }
              icon={<CloseIcon fontSize="inherit" />}
            />
          )}
        </li>
      ))}
    </ol>
  );
}

export function GraphRunnerLeaderboard(props: GraphRunnerLeaderboardProps) {
  return (
    <Box
      as="aside"
      className={styles.leaderboard}
      aria-label={`${RELATION_PATCH_GAME_TITLE} leaderboard`}
    >
      <Flex direction="column" gap="1" className={styles.leaderboardHeader}>
        <Text
          variant="body-small"
          weight="bold"
          className={styles.leaderboardTitle}
        >
          {RELATION_PATCH_LEADERBOARD_TITLE}
        </Text>
        <Text variant="body-small" className={styles.leaderboardSubtitle}>
          {RELATION_PATCH_LEADERBOARD_SUBTITLE}
        </Text>
      </Flex>

      {leaderboardBody(props)}
    </Box>
  );
}
